/**
 * ⭐ P6.19 拒绝解冻申请弹窗。
 *
 * 解冻模板要求 `min_reason_length: 20`，前端与后端双重校验：
 * 理由为空或不足 20 字时不允许提交，并在弹窗内给出明确提示。
 */

import { useEffect, useState } from 'react';
import Button from '../../components/Button';
import { ApiError } from '../../api/client';
import { useRejectUnlock } from './useApprovals';
import type { UnlockRequestItem } from './useApprovals';

/** 与后端 UNLOCK_REASON_MIN_LENGTH 一致 */
export const UNLOCK_REASON_MIN_LENGTH = 20;

export interface UnlockRejectDialogProps {
  item: UnlockRequestItem | null;
  open: boolean;
  onClose(): void;
  onDone?(): void;
}

function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'UNLOCK_REJECT_REASON_TOO_SHORT')
      return `拒绝理由至少 ${UNLOCK_REASON_MIN_LENGTH} 字，请说明原因`;
    if (err.code === 'UNLOCK_ALREADY_HANDLED') return '该解冻申请已处理';
    if (err.code === 'UNLOCK_REQUEST_NOT_FOUND') return '该解冻申请不存在或已被删除';
    if (err.code === 'UNLOCK_SELF_REJECTION_NOT_ALLOWED')
      return '你是该解冻申请的申请人，不能拒绝自己的申请，需由他人复核';
    if (err.code === 'UNLOCK_NOT_ASSIGNED_APPROVER')
      return '当前解冻节点由省管理员审批，你的角色不在审批范围内';
    return err.message;
  }
  return err instanceof Error ? err.message : '操作失败';
}

export default function UnlockRejectDialog({
  item,
  open,
  onClose,
  onDone,
}: UnlockRejectDialogProps) {
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const reject = useRejectUnlock();
  const busy = reject.isPending;

  useEffect(() => {
    if (open) {
      setComment('');
      setError(null);
      reject.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !item) return null;

  const submit = () => {
    setError(null);
    const trimmed = comment.trim();
    if (trimmed.length < UNLOCK_REASON_MIN_LENGTH) {
      setError(`拒绝理由至少 ${UNLOCK_REASON_MIN_LENGTH} 字（当前 ${trimmed.length} 字）`);
      return;
    }
    reject.mutate(
      { id: item.id, comment: trimmed },
      {
        onSuccess: () => {
          onDone?.();
          onClose();
        },
        onError: (err) => setError(friendlyError(err)),
      },
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-box modal-box--sm"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-box__title is-danger">
          拒绝解冻 · {item.taskName ?? `任务 #${item.taskId ?? '—'}`}
        </div>

        <div className="modal-box__body">
          <label className="modal-field">
            <span className="modal-field__label">
              拒绝理由（必填，至少 {UNLOCK_REASON_MIN_LENGTH} 字）
            </span>
            <textarea
              rows={4}
              value={comment}
              placeholder="请说明拒绝原因，便于申请人修改后重新申请"
              onChange={(e) => setComment(e.target.value)}
            />
          </label>

          {error && <div className="cf-error">⚠ {error}</div>}

          <div className="modal-box__foot">
            <Button variant="ghost" onClick={onClose} disabled={busy}>
              取消
            </Button>
            <Button variant="danger" onClick={submit} disabled={busy}>
              {busy ? '提交中…' : '确认拒绝'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
